/**
 * MembersPanel (H) — owner-managed access for an instance.
 *
 * Any member can view the roster. The OWNER can invite new members by email
 * (read/chat by default) — the invitee gets a sign-in link and sets their own
 * password — set a member's role (member, writer, admin), and remove members.
 * Owners are superadmin-assigned and not editable here. Session-cookie auth — no bearer.
 */

import { useCallback, useEffect, useState } from "react";
import {
  listMembers, inviteMember, elevateMember, removeMember,
  type Member, type MemberRole, type GrantableRole,
} from "../api";
import { Pill, type Tone } from "./ai/Primitives";

export function MembersPanel({ open, instance, onClose }: {
  open: boolean; instance: string; onClose: () => void;
}) {
  const [members, setMembers] = useState<Member[]>([]);
  const [you, setYou] = useState<MemberRole | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<GrantableRole>("member");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await listMembers(instance);
      setMembers(r.members);
      setYou(r.you);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  if (!open) return null;
  const isOwner = you === "owner";

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null); setNotice(null);
    const invitee = email.trim();
    try {
      const r = await inviteMember(instance, invitee, role);
      setEmail(""); setRole("member");
      setNotice(r.invited ? `Invite sent to ${invitee} — they'll get a sign-in link.` : `${invitee} added.`);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const setMemberRole = async (m: Member, next: GrantableRole) => {
    if (next === m.role) return;
    setBusy(true); setErr(null);
    try { await elevateMember(instance, m.email, next); await refresh(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const remove = async (m: Member) => {
    if (!window.confirm(`Remove ${m.email} from ${instance}?`)) return;
    setBusy(true); setErr(null);
    try { await removeMember(instance, m.email); await refresh(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">Members — <code>{instance}</code></h2>
        <p className="modal-hint">
          {isOwner
            ? "Invite people by email (read + chat by default), then set their role. They get a sign-in link and set their own password."
            : "You can view the roster. Only the instance owner can invite or change roles."}
        </p>

        {isOwner && (
          <form onSubmit={invite} style={S.form}>
            <input style={S.input} type="email" placeholder="person@company.com" value={email}
                   onChange={e => setEmail(e.target.value)} required />
            <select style={S.select} value={role} onChange={e => setRole(e.target.value as GrantableRole)}>
              {GRANTABLE.map(r => <option key={r} value={r}>{ROLE_OPTION[r]}</option>)}
            </select>
            <button className="primary" type="submit" disabled={busy}>
              {busy ? "Sending…" : "Send invite"}
            </button>
          </form>
        )}

        {notice && <div style={S.notice}>{notice}</div>}
        {err && <div className="error-banner">{err}</div>}

        {loading ? (
          <div style={S.muted}>Loading…</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr><th style={S.th}>Email</th><th style={S.th}>Role</th><th style={S.th}>Invited by</th>{isOwner && <th style={S.th}></th>}</tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.email}>
                  <td style={S.td}>{m.email}</td>
                  <td style={S.td}><Pill tone={ROLE_TONE[m.role]}>{ROLE_LABEL[m.role]}</Pill></td>
                  <td style={{ ...S.td, color: "var(--ink-3)" }}>{m.invited_by || "—"}</td>
                  {isOwner && (
                    <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                      {m.role !== "owner" && (
                        <>
                          <select style={S.rowSelect} disabled={busy} value={m.role}
                                  onChange={e => setMemberRole(m, e.target.value as GrantableRole)}>
                            {GRANTABLE.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                          </select>
                          <button className="danger" style={S.rowBtn} disabled={busy}
                                  onClick={() => remove(m)}>Remove</button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {members.length === 0 && (
                <tr><td style={{ ...S.td, color: "var(--ink-3)" }} colSpan={isOwner ? 4 : 3}>No members yet.</td></tr>
              )}
            </tbody>
          </table>
        )}

        <div className="modal-actions"><button onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

const GRANTABLE: GrantableRole[] = ["member", "writer", "admin"];
const ROLE_TONE: Record<MemberRole, Tone> = {
  owner: "running", admin: "accent", writer: "accent", member: "neutral",
};
const ROLE_LABEL: Record<MemberRole, string> = {
  owner: "Owner", admin: "Admin", writer: "Writer", member: "Member",
};
const ROLE_OPTION: Record<GrantableRole, string> = {
  member: "Member — read and chat",
  writer: "Writer — plus pipelines",
  admin: "Admin — plus API clients",
};

const S: Record<string, React.CSSProperties> = {
  form: { display: "flex", gap: 8, flexWrap: "wrap", margin: "8px 0 16px" },
  input: { flex: "1 1 160px" },
  select: { flex: "0 1 auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--line-strong)", color: "var(--ink-3)", fontWeight: 600, fontSize: 12 },
  td: { padding: "9px 8px", borderBottom: "1px solid var(--line)" },
  muted: { color: "var(--ink-3)", fontSize: 13, padding: "12px 0" },
  notice: { color: "var(--green)", fontSize: 13, margin: "0 0 12px" },
  rowBtn: { padding: "4px 10px", marginRight: 6, fontSize: 12 },
  rowSelect: { padding: "3px 6px", marginRight: 6, fontSize: 12 },
};
